import React, { useContext, useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
    Row,
    Col,
    Card,
    Button,
    Table,
    UncontrolledTooltip,
    FormGroup,
    Label,
    Input,
    Collapse,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import SearchBar from '../../../components/SearchBar.js';
import DestroyDestructionForm from './components/DestroyDestructionForm.js';
import useSearchRx from '../../../services/commonServices/useSearchRx.js';
import { SelectedFacilitiesContext } from '../../../contexts/SelectedFacilitiesContext.js';
import { UserContext } from '../../../contexts/UserContext.js';
import { ButtonLoader } from '../../../components/Loader.js';
import DropdownComponent from '../../../components/Dropdown.js';
import useSaveDestruction from '../../../services/NarcoticDestruction/useSaveDestruction.js';
import useUpdateDestruction from '../../../services/NarcoticDestruction/useUpdateDestruction.js';
import useExportFile from '../../../services/commonServices/useExportFile.js';
import usePrintPDF from '../../../services/commonServices/usePrintPDF.js';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { FaSearch, FaEdit } from 'react-icons/fa';
import { MdOutlineDeleteForever } from 'react-icons/md';
import { IoStar } from 'react-icons/io5';
import { hasRolePermission } from '../../../utility-services/rolePermissions.js';

const actionOptions = [
    { value: 'reverse distributor', label: 'Reverse Distributor' },
    { value: 'authorized disposal', label: 'Authorized Disposal' },
    { value: 'incineration', label: 'Incineration' },
    { value: 'chemical disgestion', label: 'Chemical Digestion' },
    { value: 'other', label: 'Other' },
];

const CommonFieldsValidationSchema = Yup.object({
    DestructionMethod: Yup.string().required('Method is required'),
    NurseName: Yup.string().required('Nurse Name is required'),
    WitnessName: Yup.string().required('Witness is required'),
    Specify: Yup.string(),
});

function RequiredStar() {
    return (
        <IoStar
            style={{
                color: 'red',
                fontSize: '6px',
                position: 'absolute',
                top: '0px',
            }}
        />
    );
}

function NarcoticDestruction() {
    const { selectedFacilities, facilities } = useContext(SelectedFacilitiesContext);
    const { user, roles } = useContext(UserContext);
    const canCreateNarcoticDestruction = hasRolePermission(roles, 'canCreateNarcotic');
    const canDeleteNarcoticDestruction = hasRolePermission(roles, 'canDeleteNarcotic');
    const history = useHistory();
    const location = useLocation();
    const [searchValue, setSearchValue] = useState('');
    const [searchInputValue, setSearchInputValue] = useState(null);
    const [searchAttempt, setSearchAttempt] = useState(0);
    const [searchClearTrigger, setSearchClearTrigger] = useState(0);
    const [selectedDropdownValue, setSelectedDropdownValue] = useState('');
    const [dispensedt, setDispensedt] = useState('');
    const [openDispenseDateModal, setOpenDispenseDateModal] = useState(false);
    const [pendingDestructions, setPendingDestructions] = useState([]);
    const [manualEntryMode, setManualEntryMode] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editFieldsUnlocked, setEditFieldsUnlocked] = useState(false);
    const [editNarcoticDestructionId, setEditNarcoticDestructionId] = useState(null);
    const { saveDestruction, isSaveDestructionLoading } = useSaveDestruction();
    const { updateDestruction, isUpdateDestructionLoading } = useUpdateDestruction();
    const { exportFile, isExportFileLoading } = useExportFile();
    const { printPDF, isPrintPDFLoading } = usePrintPDF();
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [activeExport, setActiveExport] = useState(null);

    const [rxLookupFacId, setRxLookupFacId] = useState('');

    const { searchRxData, isSearchRxLoading } = useSearchRx(
        searchInputValue,
        rxLookupFacId,
        user,
        dispensedt,
        setOpenDispenseDateModal,
        "/destructions/rxLookup",
        searchAttempt
    );

    const commonFormik = useFormik({
        initialValues: {
            DestructionMethod: '',
            Specify: '',
            NurseName: '',
            WitnessName: '',
        },
        validationSchema: CommonFieldsValidationSchema,
        onSubmit: () => {},
    });
    const commonValues = commonFormik.values;
    const commonErrors = commonFormik.errors;
    const commonTouched = commonFormik.touched;

    // Facility options derived from facilities
    const facilityOptions = facilities
        .filter((fac) => fac?.facName != null && fac?.facID != null)
        .map((fac) => ({ label: `(${fac.facID})  ${fac.facName} `, value: fac.facID }));

    const handleDropdownChange = (selectedValue) => {
        setSelectedDropdownValue(selectedValue);
    };
    // Auto-select facility if only one option
    useEffect(() => {
        if (facilityOptions.length === 1) {
            setSelectedDropdownValue(facilityOptions[0].value);
        }
    }, [facilities]);

    useEffect(() => {
        const editItems = location.state?.editItems;
        if (!Array.isArray(editItems) || editItems.length === 0) return;
        const editFacId = location.state?.facId ?? editItems[0]?.facId ?? '';
        const mapped = editItems.map((item, index) => ({
            id: item?.id ?? `edit-${Date.now()}-${index}`,
            rxNumber: item?.rxNumber ?? '',
            facId: item?.facId ?? editFacId,
            residentName: item?.residentName ?? '',
            residentId: item?.residentId ?? '',
            drugName: item?.drugName ?? '',
            dispensedQuantity: item?.dispensedQuantity ?? '',
            ndc: item?.ndc ?? '',
            dispenseDate: item?.dispenseDate ?? '',
            quantity: item?.quantity ?? '',
            destructionDate: item?.destructionDate ?? new Date().toISOString(),
            destructionMethod: item?.destructionMethod ?? '',
            specify: item?.specify ?? '',
            reason: item?.reason ?? '',
            nurseName: item?.nurseName ?? '',
            witnessName: item?.witnessName ?? '',
            controlledDrug: item?.controlledDrug ?? false,
        }));
        setPendingDestructions(mapped);
        setIsEditMode(true);
        setEditFieldsUnlocked(true);
        setEditNarcoticDestructionId(location.state?.narcoticDestructionId ?? null);
        if (editFacId) {
            setSelectedDropdownValue(editFacId);
        }
        commonFormik.setValues({
            DestructionMethod: mapped[0]?.destructionMethod ?? '',
            Specify: mapped[0]?.specify ?? '',
            NurseName: mapped[0]?.nurseName ?? '',
            WitnessName: mapped[0]?.witnessName ?? '',
        });
        history.replace({ pathname: location.pathname, state: {} });
    }, [location.state, location.pathname, history]);

    // Create mode: lock the header fields once the first item is added (unchanged behavior).
    // Edit mode (post-save or opened from the saved-destructions list): locked by default; the
    // user must click "Edit" to unlock the fields and the line items before they can change anything.
    const commonFieldsLocked = isEditMode ? !editFieldsUnlocked : pendingDestructions.length > 0;
    const lockedInputStyle = commonFieldsLocked ? { pointerEvents: 'none', caretColor: 'transparent' } : undefined;
    const commonFieldsComplete = Boolean(
        selectedDropdownValue &&
        commonValues.DestructionMethod &&
        commonValues.NurseName &&
        commonValues.WitnessName
    );

    const touchAllCommonFields = () => {
        commonFormik.setTouched({
            DestructionMethod: true,
            NurseName: true,
            WitnessName: true,
            Specify: true,
        });
    };

    const handleSearch = (value) => {
        if (typeof value !== 'string') {
            return;
        }
        if (!value.trim()) {
            toast.error('Please enter an Rx # before searching.');
            return;
        }
        if (!commonFieldsComplete) {
            toast.error(
                'Please select a facility and fill in Method, Nurse Name, and Witness Name before searching.'
            );
            touchAllCommonFields();
            return;
        }
        setManualEntryMode(false);
        setRxLookupFacId(selectedDropdownValue || '');
        setSearchInputValue(value);
        setSearchAttempt((prev) => prev + 1);
    };

    const hasMultipleSelectedFacilities = () => {
        const uniqueFacilityIds = new Set(
            pendingDestructions
                .map((item) => item?.facId)
                .filter((facId) => facId !== undefined && facId !== null && facId !== '')
        );

        return uniqueFacilityIds.size > 1;
    };

    const showSingleFacilityValidation = () => {
        Swal.fire({
            icon: 'info',
            text: 'Please select records from 1 facility before proceeding.',
            customClass: {
                confirmButton: 'custom-green-button',
            },
        });
    };

    const handleManualEntryClick = () => {
        if (!commonFieldsComplete) {
            toast.error(
                'Please select a facility and fill in Method, Nurse Name, and Witness Name before continuing.'
            );
            touchAllCommonFields();
            return;
        }
        setManualEntryMode((prev) => !prev);
    };

    const handleDestroyed = (row) => {
        setPendingDestructions((prev) => [...prev, row]);
    };

    const handleRemovePending = (id) => {
        setPendingDestructions((prev) => prev.filter((row) => row.id !== id));
    };

    const resetDestructionPage = () => {
        setPendingDestructions([]);
        setSearchInputValue(null);
        setSearchValue('');
        setSearchClearTrigger((prev) => prev + 1);
        setDispensedt('');
        setOpenDispenseDateModal(false);
        setRxLookupFacId('');
        setManualEntryMode(false);
        setIsEditMode(false);
        setEditFieldsUnlocked(false);
        setEditNarcoticDestructionId(null);
        commonFormik.resetForm();
    };

    // After Save, the user's next step is almost always to export what they just created, so we
    // keep them on the form in (locked) edit mode instead of resetting to a blank form. They must
    // click "Edit" to unlock fields if they actually want to change something afterward.
    // This relies on the save response returning the new record's id (and ideally its saved line
    // items with real DB ids) — if the backend doesn't return one, fall back to the old
    // reset-to-blank behavior rather than leaving the page in a broken edit state.
    const handleSaveSuccess = (data) => {
        const savedParentId =
            data?.narcoticDestructionId ?? data?.NarcoticDestructionId ?? data?.data?.narcoticDestructionId ?? null;
        const savedItems = data?.items ?? data?.data?.items ?? null;

        setSearchInputValue(null);
        setSearchValue('');
        setSearchClearTrigger((prev) => prev + 1);
        setDispensedt('');
        setOpenDispenseDateModal(false);
        setRxLookupFacId('');
        setManualEntryMode(false);

        if (!savedParentId) {
            resetDestructionPage();
            return;
        }

        const nextPendingDestructions =
            Array.isArray(savedItems) && savedItems.length === pendingDestructions.length
                ? pendingDestructions.map((row, index) => ({ ...row, id: savedItems[index]?.id ?? row.id }))
                : pendingDestructions;

        setPendingDestructions(nextPendingDestructions);
        setEditNarcoticDestructionId(savedParentId);
        setIsEditMode(true);
        setEditFieldsUnlocked(false);
    };

    const handleSaveDestruction = () => {
        if (pendingDestructions.length === 0) return;

        if (facilityOptions.length > 1 && hasMultipleSelectedFacilities()) {
            showSingleFacilityValidation();
            return;
        }

        const payload = pendingDestructions.map(({ id, ...rest }) => rest);
        saveDestruction(payload, {
            onSuccess: handleSaveSuccess,
        });
    };

    const handleUpdateDestruction = () => {
        if (pendingDestructions.length === 0) return;

        if (facilityOptions.length > 1 && hasMultipleSelectedFacilities()) {
            showSingleFacilityValidation();
            return;
        }

        // Keep real DB ids (numbers) so the backend can recognize and skip already-saved items;
        // drop client-generated string ids for newly added items.
        const items = pendingDestructions.map(({ id, ...rest }) => (typeof id === 'number' ? { id, ...rest } : rest));
        updateDestruction(
            {
                narcoticDestructionId: editNarcoticDestructionId,
                destructionMethod: commonValues.DestructionMethod,
                specify: commonValues.Specify,
                nurseName: commonValues.NurseName,
                witnessName: commonValues.WitnessName,
                items,
            },
            {
                onSuccess: resetDestructionPage,
            }
        );
    };

    // Export only ever targets items that actually exist in the DB — client-generated string ids
    // (not-yet-saved rows) are excluded.
    const getSavedItemIds = () => pendingDestructions.map((row) => row.id).filter((id) => typeof id === 'number');

    const buildExportParams = (ids) => {
        const params = new URLSearchParams();
        ids.forEach((id) => params.append('ids', id));
        params.append('exportType', 'Excel');
        return params;
    };

    const toggleExportDropdown = () => setExportDropdownOpen((prev) => !prev);

    const handleExportExcel = () => {
        const ids = getSavedItemIds();
        if (ids.length === 0) return;
        setActiveExport('excel');
        exportFile(
            { url: '/destructions/export', fileName: 'narcotic-destruction.xlsx', params: buildExportParams(ids) },
            { onSettled: () => setActiveExport(null) }
        );
    };

    const handlePrintDestruction = () => {
        const ids = getSavedItemIds();
        if (ids.length === 0) return;
        const lineItems = pendingDestructions.map((row) => ({
            rxNo: row.rxNumber,
            facId: row.facId,
            partialCount: String(row.quantity ?? ''),
            totalDestroyed: String(row.quantity ?? ''),
            method: row.destructionMethod,
            reason: row.reason,
            destroyedBy: user?.displayName,
            witnessby: row.witnessName,
        }));
        printPDF({
            url: '/destructions/generate-pdf',
            fileName: 'State_Destruction_PDF.pdf',
            downloadOnSuccess: true,
            payload: { payload: { items: lineItems }, ids },
        });
    };

    const allowDestruction = Boolean(searchRxData?.[0]);

    return (
        <div className="page-content">
            <div className="d-flex align-items-center justify-content-between">
                <h2 className="mb-2">Controlled Substance Destruction</h2>
                <Button
                    tag={Link}
                    to="/utilities/narcotic-destruction/saved"
                    style={{
                        cursor: 'pointer',
                        borderRadius: '5px',
                        padding: '8px 15px',
                        border: 'none',
                        outline: 'none',
                        backgroundColor: '#6952AD',
                        color: 'white',
                    }}>
                    Access Saved Destructions
                </Button>
            </div>

       <Row
    className="bg-white"
    style={{
        borderRadius: '7px',
        width: '100%',
        marginBottom: '1rem',
    }}
>
    <Col className="p-3">
        {/* Facility */}
        <Row>
            <Col md={6}>
                <FormGroup className="mb-0">
                    <Label>Facility Name:</Label>

                    {facilityOptions.length > 1 ? (
                        <div>
                            <span
                                id="narcotic-destruction-facility-wrapper"
                                style={{ display: 'block' }}
                            >
                                <DropdownComponent
                                    options={facilityOptions}
                                    label=""
                                    placeholder="Choose a facility"
                                    selectedDropdownValue={selectedDropdownValue}
                                    setSelectedDropdownValue={handleDropdownChange}
                                    showEmptyOption={false}
                                    emptyOptionLabel="Choose a facility"
                                    disabled={commonFieldsLocked}
                                />
                            </span>

                            {commonFieldsLocked && (
                                <UncontrolledTooltip
                                    placement="top"
                                    target="narcotic-destruction-facility-wrapper"
                                >
                                    The facility cannot be changed once items have
                                    been added.
                                </UncontrolledTooltip>
                            )}
                        </div>
                    ) : facilityOptions.length === 1 ? (
                        <div
                            className="border"
                            style={{
                                padding: '8px 12px',
                                fontWeight: '500',
                                color: '#333',
                                fontSize: '14px',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {facilityOptions[0]?.label}
                        </div>
                    ) : null}
                </FormGroup>
            </Col>
        </Row>

        <Collapse isOpen={Boolean(selectedDropdownValue)}>
            {/* Method + Specify */}
            <Row className="mt-3">
                <Col md={5}>
                    <FormGroup>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <Label for="CommonMethod">Method</Label>
                            <RequiredStar />
                        </div>
                        <DropdownComponent
                            options={actionOptions}
                            placeholder="Select method..."
                            selectedDropdownValue={commonValues.DestructionMethod}
                            setSelectedDropdownValue={(value) =>
                                commonFormik.setFieldValue('DestructionMethod', value)
                            }
                            invalid={commonErrors.DestructionMethod && commonTouched.DestructionMethod}
                            disabled={commonFieldsLocked}
                        />
                        {commonErrors.DestructionMethod && commonTouched.DestructionMethod && (
                            <div className="invalid-feedback d-block">{commonErrors.DestructionMethod}</div>
                        )}
                    </FormGroup>
                </Col>

                <Col md={5}>
                    <FormGroup>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <Label for="CommonSpecify">Specify</Label>
                        </div>
                        <Input
                            type="text"
                            name="Specify"
                            id="CommonSpecify"
                            placeholder="Specify"
                            value={commonValues.Specify}
                            onChange={commonFormik.handleChange}
                            onBlur={commonFormik.handleBlur}
                            disabled={commonFieldsLocked}
                            readOnly={commonFieldsLocked}
                            style={lockedInputStyle}
                        />
                    </FormGroup>
                </Col>
            </Row>

            {/* Nurse Name + Witness Name */}
            <Row>
                <Col md={5}>
                    <FormGroup>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <Label for="CommonNurseName">Nurse Name</Label>
                            <RequiredStar />
                        </div>
                        <Input
                            type="text"
                            name="NurseName"
                            id="CommonNurseName"
                            placeholder="Nurse Name"
                            value={commonValues.NurseName}
                            onChange={commonFormik.handleChange}
                            onBlur={commonFormik.handleBlur}
                            disabled={commonFieldsLocked}
                            readOnly={commonFieldsLocked}
                            style={lockedInputStyle}
                            invalid={commonErrors.NurseName && commonTouched.NurseName}
                        />
                        {commonErrors.NurseName && commonTouched.NurseName && (
                            <div className="invalid-feedback d-block">{commonErrors.NurseName}</div>
                        )}
                    </FormGroup>
                </Col>

                <Col md={5}>
                    <FormGroup>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <Label for="CommonWitnessName">Witness Name</Label>
                            <RequiredStar />
                        </div>
                        <Input
                            type="text"
                            name="WitnessName"
                            id="CommonWitnessName"
                            placeholder="Witness Name"
                            value={commonValues.WitnessName}
                            onChange={commonFormik.handleChange}
                            onBlur={commonFormik.handleBlur}
                            disabled={commonFieldsLocked}
                            readOnly={commonFieldsLocked}
                            style={lockedInputStyle}
                            invalid={commonErrors.WitnessName && commonTouched.WitnessName}
                        />
                        {commonErrors.WitnessName && commonTouched.WitnessName && (
                            <div className="invalid-feedback d-block">{commonErrors.WitnessName}</div>
                        )}
                    </FormGroup>
                </Col>
            </Row>
        </Collapse>
    </Col>
</Row>

            <Collapse isOpen={Boolean(selectedDropdownValue)}>
            <Row className="bg-white" style={{ borderRadius: '7px', width: '100%' }}>
                <Col className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div
                            style={{ gap: '8px', flex: '1 1 auto', minWidth: 0 }}
                            className='d-flex align-items-center justify-content-start gap-3'>

                            <div style={{ width: '40%' }}>
                                <SearchBar
                                    searchPlaceholder="Search Rx #"
                                    onSearch={handleSearch}
                                    onValueChange={setSearchValue}
                                    clearTrigger={searchClearTrigger}
                                    value={searchValue}
                                />
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2" style={{ gap: '8px', flexShrink: 0 }}>
                            <button
                                type="button"
                                className="btn btn-primary d-flex align-items-center gap-2 w-fit"
                                onClick={() => handleSearch(searchValue)}
                                disabled={!commonFieldsComplete || (isEditMode && !editFieldsUnlocked)}
                                style={{ gap: '5px', whiteSpace: 'nowrap' }}
                            >
                                <FaSearch />
                                Search
                            </button>
                            {/* <button
                                type="button"
                                className="btn btn-outline-secondary d-flex align-items-center gap-2 w-fit"
                                    onClick={handleManualEntryClick}
                                    disabled={!commonFieldsComplete || Boolean(searchInputValue)} //Disabled the button after a search is performed to prevent switching to manual entry mode
                                style={{ gap: '5px', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                                <FaEdit />
                                {manualEntryMode ? 'Cancel Manual Entry' : 'Manual Entry'}
                            </button> */}
                        </div>
                    </div>

                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            margin: '0 auto',
                        }}>
                        {isSearchRxLoading && (
                            <Card
                                style={{
                                    fontStyle: 'italic',
                                    color: '#6B7280',
                                    height: '100px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'white',
                                    borderRadius: '5px',
                                }}>
                                <ButtonLoader />
                            </Card>
                        )}
                        {manualEntryMode ? (
                            <DestroyDestructionForm
                                manualMode
                                facId={selectedDropdownValue}
                                onDestroyed={handleDestroyed}
                                canCreate={canCreateNarcoticDestruction}
                                commonValues={commonValues}
                                commonFieldsValid={commonFieldsComplete}
                            />
                        ) : (
                            allowDestruction && (
                                <DestroyDestructionForm
                                    data={searchRxData}
                                    dispensedt={dispensedt}
                                    setDispensedt={setDispensedt}
                                    openDispenseDateModal={openDispenseDateModal}
                                    setOpenDispenseDateModal={setOpenDispenseDateModal}
                                    onDestroyed={handleDestroyed}
                                    canCreate={canCreateNarcoticDestruction}
                                    commonValues={commonValues}
                                    commonFieldsValid={commonFieldsComplete}
                                />
                            )
                        )}
                        <Card className="p-2">
                            <Row>
                                <Col md={12}>
                                    <div
                                        style={{
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            overflowX: 'auto',
                                            width: '100%',
                                        }}>
                                        <Table
                                            className="table-bordered"
                                            style={{
                                                marginBottom: '0',
                                                minWidth: '1200px',
                                                whiteSpace: 'nowrap',
                                            }}>
                                            <thead
                                                className="thead-light"
                                                style={{ position: 'sticky', top: '0', zIndex: '10' }}>
                                                <tr>
                                                    <th>Rx #</th>
                                                    <th>Facility</th>
                                                    <th>Resident</th>
                                                    <th>Drug Name</th>
                                                    <th>NDC</th>
                                                    <th>Dispense Date</th>
                                                    <th>Quantity</th>
                                                    <th>Destruction Date</th>
                                                    <th>Time</th>
                                                    <th>Reason</th>
                                                    <th>Nurse</th>
                                                    <th>Witness</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pendingDestructions.length > 0 ? (
                                                    pendingDestructions.map((row) => (
                                                        <tr key={row.id}>
                                                            <td>{row.rxNumber || '-'}</td>
                                                            <td>{row.facId || '-'}</td>
                                                            <td>{row.residentName || '-'}</td>
                                                            <td>{row.drugName || '-'}</td>
                                                            <td>{row.ndc || '-'}</td>
                                                            <td>
                                                                {row.dispenseDate
                                                                    ? new Date(row.dispenseDate).toLocaleDateString()
                                                                    : '-'}
                                                            </td>
                                                            <td>{row.quantity ?? '-'}</td>
                                                            <td>
                                                                {row.destructionDate
                                                                    ? new Date(row.destructionDate).toLocaleDateString()
                                                                    : '-'}
                                                            </td>
                                                            <td>
                                                                {row.destructionDate
                                                                    ? new Date(row.destructionDate).toLocaleTimeString()
                                                                    : '-'}
                                                            </td>
                                                            <td>{row.reason || '-'}</td>
                                                            <td>{row.nurseName || '-'}</td>
                                                            <td>{row.witnessName || '-'}</td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    aria-label="Remove line"
                                                                    onClick={() => handleRemovePending(row.id)}
                                                                    disabled={isEditMode && !editFieldsUnlocked}
                                                                    style={{
                                                                        cursor: isEditMode && !editFieldsUnlocked ? 'default' : 'pointer',
                                                                        outline: 'none',
                                                                        border: 'none',
                                                                        backgroundColor: '#FFB0C4',
                                                                        borderRadius: '50%',
                                                                        padding: '5px',
                                                                        width: '30px',
                                                                        height: '30px',
                                                                        opacity: isEditMode && !editFieldsUnlocked ? 0.5 : 1,
                                                                    }}>
                                                                    <MdOutlineDeleteForever
                                                                        style={{ fontSize: '20px' }}
                                                                        color="red"
                                                                    />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={13} className="text-center p-4">
                                                            Destroyed items will appear here
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                        {pendingDestructions.length > 0 && (
                            <Row className="mt-3">
                                <Col className="d-flex justify-content-end" style={{ gap: '10px' }}>
                                    {isEditMode && (
                                        <Dropdown isOpen={exportDropdownOpen} toggle={toggleExportDropdown}>
                                            <DropdownToggle caret color="secondary" outline>
                                                Export
                                            </DropdownToggle>
                                            <DropdownMenu right>
                                                <DropdownItem onClick={handlePrintDestruction} disabled={isPrintPDFLoading}>
                                                    {isPrintPDFLoading ? 'Exporting...' : 'Print State Destruction Form'}
                                                </DropdownItem>
                                                <DropdownItem
                                                    onClick={handleExportExcel}
                                                    disabled={isExportFileLoading && activeExport === 'excel'}>
                                                    {isExportFileLoading && activeExport === 'excel'
                                                        ? 'Exporting...'
                                                        : 'Export Excel'}
                                                </DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    )}
                                    <Button
                                        color="primary"
                                        onClick={isEditMode ? handleUpdateDestruction : handleSaveDestruction}
                                        disabled={
                                            isSaveDestructionLoading ||
                                            isUpdateDestructionLoading ||
                                            (isEditMode && !editFieldsUnlocked)
                                        }>
                                        {isSaveDestructionLoading || isUpdateDestructionLoading ? (
                                            <ButtonLoader />
                                        ) : isEditMode && editFieldsUnlocked ? (
                                            'Update Destruction'
                                        ) : (
                                            'Save Destruction'
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        )}
                    </div>
                </Col>
            </Row>
            </Collapse>
        </div>
    );
}

export default NarcoticDestruction;
